<app-header></app-header>
<app-leftsidebar></app-leftsidebar>
<main class="dashboard-shell">
<div class="dashboard-page">
  @if (loading) {
    <!-- ===== Loading skeleton ===== -->
    <div class="vd-hero vd-hero--skeleton" aria-busy="true" aria-label="Loading your shop">
      <span class="vd-skel vd-skel--banner"></span>
      <div class="vd-hero-body">
        <span class="vd-skel vd-skel--logo"></span>
        <div class="vd-hero-info">
          <span class="vd-skel vd-skel--title"></span>
          <span class="vd-skel vd-skel--line"></span>
        </div>
      </div>
    </div>
    <span class="vd-skel vd-skel--tabs"></span>
    <div class="vd-tiles">
      @for (s of [1, 2, 3, 4]; track s) {
        <div class="vd-tile">
          <span class="vd-skel vd-skel--icon"></span>
          <span class="vd-skel vd-skel--num"></span>
          <span class="vd-skel vd-skel--line is-short"></span>
        </div>
      }
    </div>
    <div class="vd-panel">
      @for (s of skeletonRows; track s) {
        <div class="vd-skel-row">
          <span class="vd-skel vd-skel--thumb"></span>
          <span class="vd-skel vd-skel--line"></span>
        </div>
      }
    </div>
  } @else if (loadError) {
    <div class="dashboard-error">
      <span class="dashboard-error-icon" aria-hidden="true">
        <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="9" /><line x1="12" y1="7.5" x2="12" y2="12.5" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>
      </span>
      @if (loadError === 'signin') {
        <h3>Please sign in again</h3>
        <p>Your sign-in has expired, so we couldn't open your shop.</p>
        <a class="wt-btn-primary" routerLink="/login">Sign in</a>
      } @else {
        <h3>Couldn't open your shop</h3>
        <p>Check your connection and try again.</p>
        <button type="button" class="wt-btn-primary" (click)="load()">Try again</button>
      }
    </div>
  } @else if (vendor) {
    <!-- ===== Shop header ===== -->
    <section class="vd-hero">
      <span class="vd-hero-banner" [style.background-image]="vendor.banner ? 'url(&quot;' + vendor.banner + '&quot;)' : null"></span>
      <div class="vd-hero-body">
        @if (vendor.logo) {
          <img class="vd-hero-logo" [src]="vendor.logo" alt="" />
        } @else {
          <span class="vd-hero-logo vd-hero-logo--letter" aria-hidden="true">{{ vendor.shop_name.charAt(0).toUpperCase() }}</span>
        }
        <div class="vd-hero-info">
          <span class="vd-eyebrow">
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M3 9l1.5-5h15L21 9" /><path d="M4 9v11h16V9" /><path d="M9 20v-6h6v6" /></svg>
            Seller dashboard
          </span>
          <h1>{{ vendor.shop_name }}</h1>
          <div class="vd-hero-meta">
            <span>@{{ vendor.shop_slug }}</span>
            <span>Since {{ vendor.created_at | date: 'MMM y' }}</span>
            <span><strong>{{ publishedCount }}</strong> in store</span>
            <span><strong>{{ stats?.followers ?? vendor.follower_count ?? 0 }}</strong> follower{{ (stats?.followers ?? vendor.follower_count ?? 0) === 1 ? '' : 's' }}</span>
            @if (vendor.rating_count) {
              <span class="vd-hero-rating">
                <svg viewBox="0 0 24 24" aria-hidden="true"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" /></svg>
                <strong>{{ vendor.rating_avg | number: '1.1-1' }}</strong> ({{ vendor.rating_count }})
              </span>
            }
          </div>
        </div>
        <div class="vd-hero-actions">
          <a class="vd-btn vd-btn--ghost" [routerLink]="['/store']" [queryParams]="{ author: vendor.shop_slug }">
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8Z" /><circle cx="12" cy="12" r="3" /></svg>
            View shop
          </a>
          <button type="button" class="vd-btn" (click)="createListing()">
            <svg viewBox="0 0 24 24" aria-hidden="true"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
            Sell a new item
          </button>
        </div>
      </div>
    </section>

    @if (connectStatus && !payoutsReady) {
      <div class="vd-alert">
        <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" /></svg>
        <span>Your payout account needs attention — buyers can't pay for paid items until it's finished.</span>
        <a routerLink="/marketplace/vendor-onboarding">Finish setup</a>
      </div>
    }

    <!-- ===== Tabs ===== -->
    <div class="vd-tabs" role="tablist" aria-label="Dashboard sections">
      <button type="button" role="tab" class="vd-tab" [class.is-active]="tab === 'overview'" [attr.aria-selected]="tab === 'overview'" (click)="setTab('overview')">Overview</button>
      <button type="button" role="tab" class="vd-tab" [class.is-active]="tab === 'items'" [attr.aria-selected]="tab === 'items'" (click)="setTab('items')">
        Items <span class="vd-tab-count">{{ listings.length }}</span>
      </button>
      <button type="button" role="tab" class="vd-tab" [class.is-active]="tab === 'reviews'" [attr.aria-selected]="tab === 'reviews'" (click)="setTab('reviews')">
        Reviews <span class="vd-tab-count">{{ reviews.length }}</span>
      </button>
      <button type="button" role="tab" class="vd-tab" [class.is-active]="tab === 'coupons'" [attr.aria-selected]="tab === 'coupons'" (click)="setTab('coupons')">Coupons</button>
      <button type="button" role="tab" class="vd-tab" [class.is-active]="tab === 'profile'" [attr.aria-selected]="tab === 'profile'" (click)="setTab('profile')">Profile</button>
    </div>

    @switch (tab) {
      <!-- ===== Overview ===== -->
      @case ('overview') {
        <div class="vd-tiles">
          <div class="vd-tile">
            <span class="vd-tile-icon vd-tile-icon--gold" aria-hidden="true">
              <svg viewBox="0 0 24 24"><line x1="12" y1="1" x2="12" y2="23" /><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" /></svg>
            </span>
            <strong>{{ money(stats?.earnings) }}</strong>
            <span>Earned <em>after fees</em></span>
          </div>
          <div class="vd-tile">
            <span class="vd-tile-icon vd-tile-icon--green" aria-hidden="true">
              <svg viewBox="0 0 24 24"><circle cx="9" cy="21" r="1" /><circle cx="20" cy="21" r="1" /><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" /></svg>
            </span>
            <strong>{{ stats?.sales_count ?? 0 }}</strong>
            <span>Sales @if (stats?.free_claims) { <em>+ {{ stats?.free_claims }} free</em> }</span>
          </div>
          <div class="vd-tile">
            <span class="vd-tile-icon vd-tile-icon--blue" aria-hidden="true">
              <svg viewBox="0 0 24 24"><rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></svg>
            </span>
            <strong>{{ money(stats?.held_in_reserve) }}</strong>
            <span>
              Held in reserve
              @if (connectStatus?.reserve_active && connectStatus?.reserve_graduates_at) {
                <em>until {{ connectStatus?.reserve_graduates_at | date: 'MMM d, y' }}</em>
              }
            </span>
          </div>
          <div class="vd-tile">
            <span class="vd-tile-icon vd-tile-icon--pink" aria-hidden="true">
              <svg viewBox="0 0 24 24"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" /></svg>
            </span>
            <strong>{{ stats?.followers ?? vendor.follower_count ?? 0 }}</strong>
            <span>
              Followers
              @if (vendor.rating_count) { <em>★ {{ vendor.rating_avg | number: '1.1-1' }} rating</em> }
            </span>
          </div>
        </div>

        <div class="vd-overview">
          <div class="vd-panel">
            <div class="vd-panel-head">
              <h2>Recent sales</h2>
            </div>
            @if (stats?.recent_sales?.length) {
              <ul class="vd-sales">
                @for (sale of stats?.recent_sales; track $index) {
                  <li>
                    <span class="vd-sale-icon" [class.is-free]="!(+sale.price)" aria-hidden="true">
                      @if (+sale.price) {
                        <svg viewBox="0 0 24 24"><line x1="12" y1="1" x2="12" y2="23" /><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" /></svg>
                      } @else {
                        <svg viewBox="0 0 24 24"><polyline points="20 12 20 22 4 22 4 12" /><rect x="2" y="7" width="20" height="5" /><line x1="12" y1="22" x2="12" y2="7" /></svg>
                      }
                    </span>
                    <span class="vd-sale-main">
                      <a [routerLink]="['/marketplace/listing', sale.listing]">{{ sale.title }}</a>
                      <span>{{ sale.date | date: 'MMM d, y · h:mm a' }}</span>
                    </span>
                    <span class="vd-sale-amount">
                      @if (+sale.price) {
                        <strong>+{{ money(sale.earnings) }}</strong>
                        <span>of {{ money(sale.price) }}</span>
                      } @else {
                        <span class="vd-free">Free download</span>
                      }
                    </span>
                  </li>
                }
              </ul>
            } @else {
              <p class="vd-empty-text">No sales yet. Once someone buys or claims an item, it shows up here.</p>
            }
          </div>

          <div class="vd-panel">
            <div class="vd-panel-head">
              <h2>Payouts</h2>
            </div>
            @if (connectStatus) {
              <ul class="vd-checks">
                <li [class.is-ok]="connectStatus.charges_enabled">
                  <span class="vd-check" aria-hidden="true">
                    @if (connectStatus.charges_enabled) { <svg viewBox="0 0 24 24"><polyline points="20 6 9 17 4 12" /></svg> }
                    @else { <svg viewBox="0 0 24 24"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg> }
                  </span>
                  Buyers can pay you
                </li>
                <li [class.is-ok]="connectStatus.payouts_enabled">
                  <span class="vd-check" aria-hidden="true">
                    @if (connectStatus.payouts_enabled) { <svg viewBox="0 0 24 24"><polyline points="20 6 9 17 4 12" /></svg> }
                    @else { <svg viewBox="0 0 24 24"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg> }
                  </span>
                  Money is paid out to your bank
                </li>
              </ul>
              @if (connectStatus.reserve_active) {
                <p class="vd-note">
                  New shops have {{ connectStatus.reserve_percent | number: '1.0-0' }}% of each sale held back
                  @if (connectStatus.reserve_graduates_at) { until {{ connectStatus.reserve_graduates_at | date: 'MMM d, y' }} }
                  — it's paid to you after that.
                </p>
              }
            } @else {
              <span class="vd-skel vd-skel--line"></span>
              <span class="vd-skel vd-skel--line is-short"></span>
            }
          </div>
        </div>
      }

      <!-- ===== Items ===== -->
      @case ('items') {
        <div class="vd-panel vd-panel--flush">
          @if (listings.length === 0) {
            <div class="vd-empty">
              <p>You haven't put anything in the store yet.</p>
              <button type="button" class="wt-btn-primary" (click)="createListing()">Sell your first item</button>
              <span class="vd-hint">Create it in your Content Vault, then use <strong>Sell in store</strong> on its page.</span>
            </div>
          } @else {
            <div class="vd-items-head" aria-hidden="true">
              <span>Item</span><span>Status</span><span>Price</span><span>Sales</span><span>Earned</span><span></span>
            </div>
            @for (listing of listings; track listing.id) {
              <div class="vd-item">
                <a class="vd-item-main" [routerLink]="['/marketplace/listing', listing.id]">
                  @if (listing.cover_image) {
                    <img [src]="listing.cover_image" alt="" loading="lazy" />
                  } @else {
                    <span class="vd-item-thumb-placeholder"></span>
                  }
                  <span class="vd-item-text">
                    <span class="vd-item-title">{{ listing.title }}</span>
                    @if (listing.category_detail) { <span class="vd-item-type">{{ listing.category_detail.name }}</span> }
                  </span>
                </a>
                <span>
                  <span class="vd-status" [class]="'vd-status vd-status--' + listing.status">
                    <span class="vd-status-dot" aria-hidden="true"></span>{{ statusLabel(listing) }}
                  </span>
                </span>
                <span class="vd-item-num" data-label="Price">{{ +listing.price ? money(listing.price) : 'Free' }}</span>
                <span class="vd-item-num" data-label="Sales">
                  {{ itemSales(listing).sales }}@if (itemSales(listing).claims) { <em> +{{ itemSales(listing).claims }} free</em> }
                </span>
                <span class="vd-item-num" data-label="Earned">{{ money(itemSales(listing).earnings) }}</span>
                <span class="vd-item-actions">
                  <button type="button" class="vd-icon-btn" (click)="editListing(listing)" title="Edit" aria-label="Edit item">
                    <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 20h9" /><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" /></svg>
                  </button>
                </span>
              </div>
            }
          }
        </div>
      }

      <!-- ===== Reviews ===== -->
      @case ('reviews') {
        <div class="vd-panel">
          @if (reviews.length === 0) {
            <p class="vd-empty-text">No one has reviewed your items yet.</p>
          } @else {
            @for (review of reviews; track review.id) {
              <div class="vd-review">
                <div class="vd-review-head">
                  <span class="vd-review-avatar" aria-hidden="true">{{ (review.reviewer_display || 'A').charAt(0).toUpperCase() }}</span>
                  <div class="vd-review-who">
                    <span class="vd-review-author">{{ review.reviewer_display }}</span>
                    <span class="vd-review-date">{{ review.created_at | date: 'MMM d, y' }}</span>
                  </div>
                  <span class="vd-review-stars" [attr.aria-label]="review.rating + ' of 5 stars'">
                    @for (on of starsFor(review.rating); track $index) {
                      <svg viewBox="0 0 24 24" [class.is-on]="on" aria-hidden="true"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" /></svg>
                    }
                  </span>
                </div>
                @if (review.body) {
                  <p class="vd-review-body">{{ review.body }}</p>
                }

                @if (review.vendor_response && !isResponding(review)) {
                  <div class="vd-review-response">
                    <strong>Your reply</strong>
                    <p>{{ review.vendor_response }}</p>
                    <button type="button" class="vd-text-btn" (click)="startResponse(review)">Edit reply</button>
                  </div>
                } @else if (!isResponding(review)) {
                  <button type="button" class="vd-text-btn" (click)="startResponse(review)">Reply</button>
                }

                @if (isResponding(review)) {
                  <div class="vd-review-form">
                    <textarea
                      rows="3"
                      placeholder="Reply publicly to this review"
                      [(ngModel)]="responseDrafts[review.id]"
                      [name]="'response' + review.id"
                    ></textarea>
                    <div class="vd-review-actions">
                      <button
                        type="button"
                        class="wt-btn-primary"
                        [disabled]="respondingId === review.id || !responseDrafts[review.id]?.trim()"
                        (click)="submitResponse(review)"
                      >{{ respondingId === review.id ? 'Posting…' : 'Post reply' }}</button>
                      <button type="button" class="vd-text-btn" (click)="cancelResponse(review)">Cancel</button>
                    </div>
                  </div>
                }
              </div>
            }
          }
        </div>
      }

      <!-- ===== Coupons ===== -->
      @case ('coupons') {
        <div class="vd-panel">
          <div class="vd-coupons-head">
            <div>
              <h2>Discount codes</h2>
              <p>Share a code with your fans. It takes money off <strong>your</strong> items only, and you pay for the discount (the platform fee is taken from the lower price).</p>
            </div>
            @if (!couponFormOpen) {
              <button type="button" class="vd-btn" (click)="openCouponForm()">
                <svg viewBox="0 0 24 24" aria-hidden="true"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
                New code
              </button>
            }
          </div>

          @if (couponFormOpen) {
            <form class="vd-coupon-form" (ngSubmit)="saveCoupon()">
              <label class="vd-cf-field vd-cf-field--code">
                <span>Code</span>
                <input type="text" name="code" [(ngModel)]="couponDraft.code" maxlength="40" placeholder="e.g. SPRING20" autocomplete="off" />
                @if (couponErrors['code']) { <em class="vd-cf-error">{{ couponErrors['code'] }}</em> }
              </label>
              <div class="vd-cf-field">
                <span>Discount</span>
                <div class="vd-cf-amount">
                  <div class="vd-cf-type" role="radiogroup" aria-label="Discount type">
                    <button type="button" [class.is-on]="couponDraft.discount_type === 'percent'" (click)="couponDraft.discount_type = 'percent'">%</button>
                    <button type="button" [class.is-on]="couponDraft.discount_type === 'fixed'" (click)="couponDraft.discount_type = 'fixed'">$</button>
                  </div>
                  <input type="number" name="value" [(ngModel)]="couponDraft.discount_value" min="0.01" [max]="couponDraft.discount_type === 'percent' ? 100 : null" step="0.01"
                    [placeholder]="couponDraft.discount_type === 'percent' ? '20' : '5.00'" />
                </div>
                @if (couponErrors['discount_value']) { <em class="vd-cf-error">{{ couponErrors['discount_value'] }}</em> }
              </div>
              <label class="vd-cf-field">
                <span>Max uses <i>(optional)</i></span>
                <input type="number" name="max" [(ngModel)]="couponDraft.max_redemptions" min="1" step="1" placeholder="No limit" />
                @if (couponErrors['max_redemptions']) { <em class="vd-cf-error">{{ couponErrors['max_redemptions'] }}</em> }
              </label>
              <label class="vd-cf-field">
                <span>Ends on <i>(optional)</i></span>
                <input type="date" name="expires" [(ngModel)]="couponDraft.expires_at" [min]="todayIso" />
              </label>
              @if (couponErrors['non_field_errors']) { <p class="vd-cf-error vd-cf-error--wide">{{ couponErrors['non_field_errors'] }}</p> }
              <div class="vd-cf-actions">
                <button type="button" class="vd-text-btn" (click)="couponFormOpen = false">Cancel</button>
                <button type="submit" class="wt-btn-primary" [disabled]="savingCoupon || !couponDraft.code.trim() || !couponDraft.discount_value">
                  {{ savingCoupon ? 'Creating…' : 'Create code' }}
                </button>
              </div>
            </form>
          }

          @if (!couponsLoaded) {
            @for (s of skeletonRows; track s) {
              <div class="vd-skel-row"><span class="vd-skel vd-skel--line"></span></div>
            }
          } @else if (coupons.length === 0 && !couponFormOpen) {
            <div class="vd-empty">
              <p>You haven't made any discount codes yet.</p>
              <span class="vd-hint">Codes are a great way to reward followers or run a launch sale.</span>
            </div>
          } @else {
            <ul class="vd-coupons">
              @for (coupon of coupons; track coupon.id) {
                <li class="vd-coupon" [class.is-off]="couponState(coupon) !== 'active'">
                  <button type="button" class="vd-coupon-code" (click)="copyCode(coupon)" title="Copy code">
                    {{ coupon.code }}
                    <svg viewBox="0 0 24 24" aria-hidden="true"><rect x="9" y="9" width="13" height="13" rx="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" /></svg>
                  </button>
                  <span class="vd-coupon-value">{{ couponLabel(coupon) }}</span>
                  <span class="vd-coupon-meta">
                    Used {{ coupon.times_redeemed }}{{ coupon.max_redemptions != null ? ' / ' + coupon.max_redemptions : '' }}
                    @if (coupon.expires_at) { · ends {{ coupon.expires_at | date: 'MMM d, y' }} }
                  </span>
                  <span [class]="'vd-status vd-coupon-state--' + couponState(coupon)">
                    <span class="vd-status-dot" aria-hidden="true"></span>
                    {{ couponStateLabel(coupon) }}
                  </span>
                  <span class="vd-coupon-actions">
                    @if (couponState(coupon) === 'active' || couponState(coupon) === 'paused') {
                      <button type="button" class="vd-text-btn" [disabled]="busyCouponId === coupon.id" (click)="toggleCoupon(coupon)">
                        {{ coupon.active ? 'Pause' : 'Turn on' }}
                      </button>
                    }
                    <button type="button" class="vd-icon-btn" [disabled]="busyCouponId === coupon.id" (click)="deleteCoupon(coupon)" title="Delete" aria-label="Delete code">
                      <svg viewBox="0 0 24 24" aria-hidden="true"><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /></svg>
                    </button>
                  </span>
                </li>
              }
            </ul>
          }
        </div>
      }

      <!-- ===== Shop profile ===== -->
      @case ('profile') {
        <section class="shop-profile" id="shop-profile">
          <div class="shop-profile-head">
            <h2>Shop profile</h2>
            <p>How your shop looks to buyers on its page and in All Shops.</p>
          </div>

          <div class="shop-profile-preview">
            <div class="sp-banner" [style.background-image]="bannerPreview ? 'url(&quot;' + bannerPreview + '&quot;)' : null">
              @if (preparing.banner) {
                <span class="sp-preparing"><span class="sp-spinner" aria-hidden="true"></span> Preparing picture…</span>
              }
              <div class="sp-banner-actions">
                <label class="sp-pic-btn">
                  <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" /><circle cx="12" cy="13" r="4" /></svg>
                  {{ bannerPreview ? 'Change banner' : 'Add banner' }}
                  <input type="file" accept="image/*" (change)="pickPicture('banner', $event)" hidden />
                </label>
                @if (bannerPreview) {
                  <button type="button" class="sp-pic-btn sp-pic-btn--icon" (click)="removePicture('banner')" aria-label="Remove banner" title="Remove banner">
                    <svg viewBox="0 0 24 24" aria-hidden="true"><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6M14 11v6" /><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" /></svg>
                  </button>
                }
              </div>
            </div>
            <div class="sp-logo-wrap">
              @if (logoPreview) {
                <img class="sp-logo" [src]="logoPreview" alt="Shop logo" />
              } @else {
                <span class="sp-logo sp-logo--letter" aria-hidden="true">{{ (profileName || vendor.shop_name || '?').charAt(0).toUpperCase() }}</span>
              }
              <label class="sp-logo-edit" title="Change logo">
                @if (preparing.logo) {
                  <span class="sp-spinner sp-spinner--dark" aria-hidden="true"></span>
                } @else {
                  <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 20h9" /><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" /></svg>
                }
                <span class="visually-hidden">Change logo</span>
                <input type="file" accept="image/*" (change)="pickPicture('logo', $event)" hidden />
              </label>
              @if (logoPreview) {
                <button type="button" class="sp-logo-remove" (click)="removePicture('logo')">Remove logo</button>
              }
            </div>
          </div>

          <div class="shop-profile-form">
            <label class="sp-field">
              <span>Shop name</span>
              <input type="text" maxlength="150" [(ngModel)]="profileName" name="shopName" placeholder="Your shop's name" />
            </label>
            <label class="sp-field">
              <span>About your shop <em>{{ profileBio.length }}/1000</em></span>
              <textarea
                rows="4"
                maxlength="1000"
                [(ngModel)]="profileBio"
                name="shopBio"
                placeholder="What do you make? Which systems and settings? A few lines buyers will read on your shop page."
              ></textarea>
            </label>
            <p class="sp-hint">Any picture works — big photos are resized for you. A wide banner (about 1600 × 400) looks best.</p>
            <div class="sp-actions">
              <button type="button" class="wt-btn-primary" [disabled]="!profileDirty || savingProfile || !profileName.trim() || preparing.logo || preparing.banner" (click)="saveProfile()">
                {{ savingProfile ? 'Saving…' : 'Save changes' }}
              </button>
              @if (profileDirty && !savingProfile) {
                <button type="button" class="vd-text-btn" (click)="resetProfile()">Discard</button>
              }
            </div>
          </div>
        </section>
      }
    }
  }
</div>
</main>
<app-footer></app-footer>
